import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUser, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { _Object } from 'utils/interfaces';
import { routes, strings } from 'utils';
import Modal from './modal';
import { Button } from '../../../components';
import { SearchField } from 'views/components/form-inputs';
import { userService } from 'services';
import { setCustomer } from 'redux/slices/cart.slice';

interface CustomerProps {
  moreMenu?: React.ReactNode;
}

const Customer = ({ moreMenu }: CustomerProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const location = useLocation();

  const [loading, setLoading] = useState<any>({
    search: false,
    btn: false,
    recent: false,
  });

  const { cart }: any = useSelector((state: _Object) => state.cart);
  const dispatch = useDispatch();

  const selectRef = useRef<any>(null);
  const [users, setUsers] = useState<_Object[]>([]);
  const [isFormSubmit, setIsFormSubmit] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const customerModalId = 'customer-modal-sales';

  useEffect(() => {
    if (selectRef.current && selectRef.current.state && !selectRef.current.state.isFocused) {
      selectRef.current.focus();
    }
  }, []);

  const formik = useFormik({
    initialValues: { q: '' },
    enableReinitialize: true,
    onSubmit: (values: any) => {
      setIsFormSubmit(true);
      setLoading((prev: any) => ({ ...prev, search: true }));
      userService.getCustomerSearch({ q: values.q }).then((result: any) => {
        setUsers(result?.items || []);
        setLoading((prev: any) => ({ ...prev, search: false }));
      });
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (users.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex === null || prevIndex === users.length - 1 ? 0 : prevIndex + 1
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex === null || prevIndex === 0 ? users.length - 1 : prevIndex - 1
        );
      } else if (e.key === 'Enter' && selectedIndex !== null) {
        e.preventDefault();
        dispatch(setCustomer(users[selectedIndex]));
      }
    }
  };

  const handleClick = () => {
    dispatch(setCustomer({}));
    setSelectedIndex(null);
    const getId = document.getElementById('customerSearchField');
    if (getId) getId.focus();
  };

  const closeOffcanvas = () => {
    const btn = document.querySelector('#userSearch [data-bs-dismiss="offcanvas"]') as HTMLElement;
    btn?.click();
  };

  return (
    <>
      <div className="customer-list">
        {location.pathname === `${routes.sales}` && !cart.customer?.id && (
          <form
            onSubmit={formik.handleSubmit}
            onKeyDown={handleKeyDown}
            className={`form-loading ${loading.search ? 'btn-loading' : ''}`}
          >
            <div className="d-flex align-items-stretch gap-2 mb-3">
              <div className="flex-grow-1">
                <SearchField
                  name="q"
                  disabled={!formik.values.q}
                  id="customerSearchField"
                  autoFocus
                  type="search"
                  autoComplete="off"
                  className="mb-0"
                  placeholder={t('customer.search_customer') || 'Search customer...'}
                  value={formik.values.q}
                  onChange={(e: any) => {
                    formik.handleChange(e);
                    setIsFormSubmit(false);
                  }}
                />
              </div>

              <Button
                type="button"
                className="light flex-shrink-0 d-flex align-items-center justify-content-center"
                style={{ width: '42px', minWidth: '42px', height: '42px', padding: 0 }}
                data-bs-toggle="modal"
                data-bs-target={`#${customerModalId}`}
              >
                <FontAwesomeIcon icon={faPlus} className="me-0" />
              </Button>

              {moreMenu}
            </div>
            <ul className="list-group">
              {users?.map((data: any, i: number) => {
                const makeAvatar = (firstName: string, lastName: string) => {
                  const capitalize = (name: string) =>
                    name?.charAt(0)?.toUpperCase() + name?.slice(1)?.toLowerCase();
                  const firstInitial = capitalize(firstName || '')[0] || '';
                  const lastInitial = capitalize(lastName || '')[0] || '';
                  return `${firstInitial}${lastInitial}`;
                };

                const avatar = makeAvatar(data?.first_name, data?.last_name);

                return (
                  <li
                    key={i}
                    className={`list-group-item ${selectedIndex === i ? 'active' : ''}`}
                    aria-current="true"
                    onClick={() => {
                      dispatch(setCustomer(data));
                      closeOffcanvas();
                    }}
                  >
                    <div className="d-flex">
                      <div className="rounded-circle bg-light my-auto avtar">
                        <span className="mb-0">{avatar}</span>
                      </div>
                      <aside className="ms-2">
                        <span>{data?.full_name || data.mobile_number}</span>
                        <h6 className="m-0">{data.mobile_number}</h6>
                      </aside>
                    </div>
                  </li>
                );
              })}
            </ul>
          </form>
        )}

        {cart.customer && (
          <ul className="list-group list-group-flush overflow-auto">
            {!cart.customer.id ? (
              <>
                {formik.values.q && users?.length === 0 && isFormSubmit && !loading.search && (
                  <div>
                    <p className="text-danger text-center">{strings.noRecordFound}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="pos-customer-card d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center gap-2 overflow-hidden">
                  <div className="pos-customer-avatar">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                  <div className="pos-customer-info overflow-hidden">
                    <span className="pos-customer-name text-truncate d-block">
                      {cart.customer.full_name ||
                        cart.customer.name ||
                        `${cart.customer.first_name || ''} ${cart.customer.last_name || ''}`.trim() ||
                        'Customer'}
                    </span>
                    <span className="pos-customer-details text-truncate d-block">
                      {cart.customer.mobile_number || cart.customer.phone || 'Customer details'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted p-0 ms-1 flex-shrink-0"
                  title="Clear customer"
                  onClick={handleClick}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            )}
          </ul>
        )}
      </div>

      <Modal
        modalId={customerModalId}
        value={formik.values.q || searchText}
        setSearchText={setSearchText}
      />
    </>
  );
};

export default Customer;